import { Trans, useLingui } from '@lingui/react';
import { Table } from '@startuptools/ui/Table';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { SccsTableRowFooter } from './SccsTableRowFooter';
import { GqlCurrency, type GqlCompleteShareCapitalChange } from '../../../graphql/base-types.graphql';

const DECIMALS = 6;

export const SccsSplitAdjustedTable = ({
  rows,
  totalPaid,
}: {
  rows: GqlCompleteShareCapitalChange[];
  totalPaid: { [k in GqlCurrency]?: number };
}) => {
  const { i18n } = useLingui();

  return (
    <Table sx={{ display: 'block' }} noWrap>
      <thead>
        <tr>
          <td>
            <Trans id="Decision date" />
          </td>
          <td>
            <Trans id="Registration date" />
          </td>
          <td>
            <Trans id="Change" />
          </td>
          <td align="right">
            <Trans id="Changed Number Of Shares (Split Adjusted)" />
          </td>
          <td align="right">
            <Trans id="Number Of Shares (Split Adjusted)" />
          </td>
          <td align="right">
            <Trans id="Price Per Share (Split Adjusted)" />
          </td>
          <td align="right">
            <Trans id="Pre-money" />
          </td>
          <td align="right">
            <Trans id="Paid Amount" />
          </td>
          <td align="right">
            <Trans id="Post-money" />
          </td>
          <td align="right">
            <Trans id="Share Capital Before" />
          </td>
          <td align="right">
            <Trans id="Share Capital Changed" />
          </td>
          <td align="right">
            <Trans id="Share Capital After" />
          </td>
          <td align="right">
            <Trans id="Share Price Factor" />
          </td>
          <td align="right">
            <Trans id="Case Number" />
          </td>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={index}>
            <td>{row.decisionDate ? <DateFormat locale={i18n.locale}>{row.decisionDate}</DateFormat> : ''}</td>
            <td>{row.registrationDate ? <DateFormat locale={i18n.locale}>{row.registrationDate}</DateFormat> : ''}</td>
            <td>{row.name}</td>
            <td align="right">
              <NumberFormat maximumFractionDigits={0} locale={i18n.locale}>
                {row.changedNumberOfSharesSplitAdjusted}
              </NumberFormat>
            </td>
            <td align="right">
              <NumberFormat maximumFractionDigits={0} locale={i18n.locale}>
                {row.numberOfSharesSplitAdjusted}
              </NumberFormat>
            </td>
            <td align="right">
              {row.pricePerShareSplitAdjusted && !isNaN(+row.pricePerShareSplitAdjusted) ? (
                <NumberFormat
                  variant="currency"
                  locale={i18n.locale}
                  currency={row.currency ?? 'SEK'}
                  minimumFractionDigits={DECIMALS}
                  maximumFractionDigits={DECIMALS}
                >
                  {row.pricePerShareSplitAdjusted}
                </NumberFormat>
              ) : (
                ''
              )}
            </td>
            <td align="right">
              <NumberFormat
                variant="currency"
                locale={i18n.locale}
                currency={row.currency ?? 'SEK'}
                minimumFractionDigits={DECIMALS}
                maximumFractionDigits={DECIMALS}
              >
                {row.preMoney}
              </NumberFormat>
            </td>
            <td align="right">
              <NumberFormat
                variant="currency"
                locale={i18n.locale}
                currency={row.currency ?? 'SEK'}
                minimumFractionDigits={DECIMALS}
                maximumFractionDigits={DECIMALS}
              >
                {row.paidAmount}
              </NumberFormat>
            </td>
            <td align="right">
              <NumberFormat
                variant="currency"
                locale={i18n.locale}
                currency={row.currency ?? 'SEK'}
                minimumFractionDigits={DECIMALS}
                maximumFractionDigits={DECIMALS}
              >
                {row.postMoney}
              </NumberFormat>
            </td>
            <td align="right">
              <NumberFormat
                variant="currency"
                locale={i18n.locale}
                currency={row.currency ?? 'SEK'}
                minimumFractionDigits={DECIMALS}
                maximumFractionDigits={DECIMALS}
              >
                {row.shareCapitalBefore}
              </NumberFormat>
            </td>
            <td align="right">
              <NumberFormat
                variant="currency"
                locale={i18n.locale}
                currency={row.currency ?? 'SEK'}
                minimumFractionDigits={DECIMALS}
                maximumFractionDigits={DECIMALS}
              >
                {row.shareCapitalChanged}
              </NumberFormat>
            </td>
            <td align="right">
              <NumberFormat
                variant="currency"
                locale={i18n.locale}
                currency={row.currency ?? 'SEK'}
                minimumFractionDigits={DECIMALS}
                maximumFractionDigits={DECIMALS}
              >
                {row.shareCapitalAfter}
              </NumberFormat>
            </td>
            <td align="right">
              <NumberFormat variant="number" locale={i18n.locale}>
                {row.sharePriceFactor}
              </NumberFormat>
            </td>
            <td align="right">{row.caseNumber}</td>
          </tr>
        ))}
      </tbody>
      <SccsTableRowFooter totalPaid={totalPaid} preCells={6} postCells={6} />
    </Table>
  );
};
